"use strict";

import axios from "axios";
import {retryStaleSession} from "../utils/stale-session-retry";
import store from "../state/store";
import router from "../router";
import { requestWriteAccess } from "@/utils/tenant-access-prompt";
import { sendsPlatformView } from "@/utils/platform-view";
import { sessionEnded, markSessionEnded, clearSessionEnded } from "@/utils/session-state";
import { isEmbedded, xsrfCookieName, notifyExpired, embedState } from "@/utils/embed";
import { noteSaved, noteWriteFailed, writeOrigin } from "@/utils/unsaved-changes";

// Full config:  https://github.com/axios/axios#request-config
// axios.defaults.baseURL = process.env.baseURL || process.env.apiUrl || '';
// axios.defaults.headers.common['Authorization'] = AUTH_TOKEN;
// axios.defaults.headers.post['Content-Type'] = 'application/x-www-form-urlencoded';

// Normalize a JS boolean to 1/0 (what Laravel's `boolean` rule accepts); pass
// anything else through unchanged. Shared by the FormData and PATCH encoders.
const bool = (value) => typeof value === 'boolean' ? (value === true ? 1 : 0) : value;

function objToFormData (obj = {}, formData = new FormData(), key = '') {
    if (!([Array, File, Object].includes(obj.constructor))) {
        return formData;
    }

    // Handle File recursive
    if (obj instanceof File) {
        formData.append(key, obj);
        return formData;
    }

    for (const prop in obj) {
        // Validate value type
        if (obj[prop] && !([String, Number, Boolean, Array, Object, File].includes(obj[prop].constructor))) {
            continue;
        }

        // Set deep index of prop
        const deepKey = key ? key + `[${prop}]` : prop;

        // Handle array
        if (Array.isArray(obj[prop])) {
            if (obj[prop].length) {
                obj[prop].forEach((item, index) => {
                    if (typeof item === 'object') {
                        objToFormData(item, formData, `${deepKey}[${index}]`);
                    } else {
                        formData.append(deepKey + `[${index}]`, bool(item))
                    }
                });
                continue;
            } else {
                // formData.append(`${deepKey}[0]`, '')
                // continue;
            }
        }

        (typeof obj[prop] === 'object' && obj[prop] && obj[prop].constructor === Object)
            ? objToFormData(obj[prop], formData, deepKey) // Handle object
            : formData.append(deepKey, [undefined, null].includes(obj[prop]) ? '' : bool(obj[prop])) // Handle string, number, boolean
    }

    return formData;
}

const apiUrl = window.__env.api.url;

let config = {
    baseURL: apiUrl || process.env.baseURL || process.env.apiUrl || '',
    transformRequest: [function (data, headers) {
        // Nagy, egymásba ágyazott adat JSON-ként (pl. egy kérdőív a kérdéseivel): űrlap-mezőkként a PHP
        // max_input_vars (1000) fölött a maradékot csendben eldobja. A `_method` itt fejlécben megy.
        if (data && data.__json === true) {
            const body = {...data};
            delete body.__json;
            if (body._method) headers['X-HTTP-Method-Override'] = body._method;
            headers['Content-Type'] = 'application/json';

            return JSON.stringify(body);
        }

        if (this.method !== 'patch') {
            return (data instanceof FormData ? data : objToFormData(data));
        }

        // PATCH bodies are form-urlencoded (PHP doesn't populate the request from a
        // multipart PATCH). Serialize scalars exactly like objToFormData does:
        // booleans -> 1/0, null/undefined -> '' — so Laravel's boolean/nullable rules
        // accept them (a raw boolean would otherwise become the string "true"/"false"
        // and fail the `boolean` rule with a 422).
        const params = [];

        for (const prop in data) {
            const value = data[prop];
            params.push(prop + '=' + encodeURIComponent([undefined, null].includes(value) ? '' : bool(value)));
        }

        return params.join('&');
    }],
    // timeout: 60 * 1000, // Timeout
    withCredentials: true, // Check cross-site Access-Control
};

// lejárt munkamenet: a háttérkérések egyszerre kapják a 401-et, egy üzenet és egy átirányítás menjen
let sessionExpiredRedirecting = false;
const PUBLIC_REQUEST = /^\/?(api\/)?(login|logout|forgot-password|reset-password|two-factor|sanctum\/|csrf-cookie|translation|public\/|users\/link-status)/;
const isPublicRequest = (url) => PUBLIC_REQUEST.test(String(url || '').replace(/^https?:\/\/[^/]+/, ''));

const _axios = axios.create(config);

_axios.interceptors.request.use(
    function(config) {
        // melyik űrlap mentése: a sikeres válasz csak annak az alapállapotát frissíti
        if (!['get', 'head', 'options'].includes(String(config.method).toLowerCase())) {
            config.unsavedOrigin = writeOrigin();
        }

        if (sessionEnded() && !isPublicRequest(config.url)) {
            return Promise.reject(new axios.Cancel('session ended'));
        }

        // Runtime (config/config.js: window.__env.spa.id) felülírhatja a build-time
        // app-azonosítót — így ugyanaz a build más-más applikációként futhat
        // domainenként (tenant-kötött appok); fallback a beégetett env. Szándékosan
        // || (nem ??): üres/falsy spa.id félrekonfiguráció → a default app-ra esünk.
        const spaId = window.__env?.spa?.id || process.env.VUE_APP_CLIENT_ID;
        // Ha egyik sincs (build-hiba: hiányzó VUE_APP_CLIENT_ID), a fejléc MARADJON EL —
        // a szó szerinti "undefined" a szerveren azonosítóként menne le, és a bejelentkezés
        // előtt hasalna el, a hibaüzenetből pedig nem látszana, hogy a build a hibás.
        if (spaId) {
            config.headers['X-SPA-ID'] = spaId;
        }
        if (sendsPlatformView(config.method)) {
            config.headers['X-Platform-View'] = '1';
        }
        // Do something before request is sent
        config.headers.Accept = 'application/json'
        // A Globalixba ágyazott keret (#273): a szerver ebből tudja, hogy a keret saját munkamenet- és
        // CSRF-sütijét kell olvasnia. A localStorage tokenje a keretben nem mehet: élesen a normál fülé.
        if (isEmbedded()) {
            config.headers['X-XMED-Embed'] = '1';
            // a lap saját intézménye: egy másik Globalix-lap más intézménnyel átírhatja a közös munkamenetet
            const tenantId = embedState()?.tenant_id;
            if (tenantId) {
                config.headers['X-XMED-Embed-Tenant'] = String(tenantId);
            }
        } else if (localStorage.getItem('accessToken')) {
            config.headers.Authorization = 'Bearer ' + localStorage.getItem('accessToken')
        }

        // Ha már létezik a header, nem írjuk felül
        if (!config.headers['X-XSRF-TOKEN']) {

            // Cookie kiolvasása natívan
            const cookieName = xsrfCookieName() + '=';
            const token = document.cookie
                .split('; ')
                .find(row => row.startsWith(cookieName))
                ?.split('=')[1];
            if (token) {
                // Laravel URL-safe base64-et ad → decode kell
                config.headers['X-XSRF-TOKEN'] = decodeURIComponent(token);
            }
        }

        return config;
    },
    function(error) {
        // Do something with request error
        return Promise.reject(error);
    }
);

// Add a response interceptor
let passwordExpiryHandled = false;

_axios.interceptors.response.use(
    function(response) {
        // sikeres mentés: az utána induló navigáció (vissza a listára) nem kérdez a nem mentett változásról
        if (!['get', 'head', 'options'].includes(String(response.config?.method).toLowerCase()) && !/userListPreferences/.test(response.config?.url || '')) {
            noteSaved(response.config?.unsavedOrigin);
        }

        // Do something with response data
        if (response.data && response.data.access_token && !isEmbedded()) {
            localStorage.setItem('accessToken', response.data.access_token)
            clearSessionEnded();
        }
        return response;
    },
    function(error) {
        // Munkamenet-csere közben elkésett kérés (belépés, SSO, átjelentkezés): a szerver
        // `X-Session-Stale`-lel jelzi, hogy nem kijelentkezés. Újraküldjük, nem léptetünk ki.
        const staleRetry = retryStaleSession(_axios, error);
        if (staleRetry) {
            return staleRetry;
        }

        // elutasított mentés (422, szerverhiba): a „Mentés és bezárás" ilyenkor az űrlapon marad
        const status = error.response?.status;
        if ((status === 422 || status >= 500) && !['get', 'head', 'options'].includes(String(error.config?.method).toLowerCase())) {
            noteWriteFailed();
        }

        // Élő munkamenet egy vendég-oldalon (belépés, elfelejtett jelszó, jelszó-beállító link): a szerver
        // 409 `already_authenticated`-et ad. Kilépés, és a kérés egyszer újra megy (l. RedirectIfAuthenticated).
        if (error.response?.status === 409 && error.response.data?.code === 'already_authenticated' && !error.config?._signedOutRetry) {
            // a kilépés új XSRF-sütit ad: a régi fejléc nem mehet újra (419), a kérés-interceptor a sütiből teszi vissza
            const headers = {...error.config.headers};
            delete headers['X-XSRF-TOKEN'];

            return store.dispatch('auth/logout').then(() => _axios({...error.config, headers, _signedOutRetry: true}));
        }

        // Lejárt jelszó menet közben: a munkamenet közben is átfordulhat a határon. A
        // szerver ilyenkor mindent 423-mal utasít el; a felület ebből tudja meg, hogy a
        // jelszó-csere képernyőt kell mutatnia. Egyszer kérdezünk vissza, nem hívásonként.
        if (error.response?.status === 423 && error.response?.data?.code === 'password_expired') {
            // Ha a felület már tudja (a jelszó-képernyő fent van), nincs mit visszakérdezni: a
            // háttérben futó hívások (értesítés-pollozás) különben minden 423-nál újrakérnék.
            const alreadyKnown = !!store.getters['user/authenticated']?.password_policy?.must_change;

            if (!alreadyKnown && !passwordExpiryHandled) {
                passwordExpiryHandled = true;
                store.dispatch('user/authenticated').finally(() => {
                    passwordExpiryHandled = false;
                });
            }

            return Promise.reject(error);
        }

        // Belépett global admin, bekapcsolt írás nélkül (vagy lejárt írással): a szerver
        // minden írást 403-mal utasít el. Felugró ablak kéri az írást (indok, időkorlát);
        // bekapcsolás után az eredeti kérés magától újra lefut. Ha elveti, a szerver
        // üzenete marad, és a felület is csak-olvasásra áll vissza.
        if (error.response?.status === 403 && error.response?.data?.code === 'tenant_read_only') {
            if (!error.config?._tenantWriteRetried && store.getters['user/authenticated']?.tenant_context?.entered) {
                return requestWriteAccess().then(granted => {
                    if (!granted) {
                        return Promise.reject(error);
                    }

                    return _axios({...error.config, _tenantWriteRetried: true});
                });
            }

            store.commit('app/ADD_ALERT', {
                message: error.response.data.message,
                style: 'warning',
                icon: 'warning',
                countdown: 6000,
            }, { root: true });
            store.dispatch('user/authenticated');
            store.dispatch('tenant/fetchMine', true).catch(() => {});

            return Promise.reject(error);
        }

        // A Globalix-beléptetés elutasítása (lejárt / már felhasznált token) nem lejárt munkamenet:
        // az oldal maga mutatja a szerver üzenetét. A kijelentkeztetés itt egy közben sikeres
        // belépést dobna el.
        const ssoRedeem = String(error.config?.url || '').includes('public/sso/globalix');

        if (error.response && [401, 419].includes(error.response.status) && !ssoRedeem) {
            store.dispatch('auth/logoutOnlyFrontend')
            // A keretben nincs belépő oldal: üzenet, és a Globalix új tokennel nyitja újra (#273).
            if (isEmbedded()) {
                // A belépés még nem bizonyosodott meg (a böngésző el sem tárolta a keret sütijét, pl. Safari a
                // HTTP-s teszt-Globalix alatt): ez nem lejárat, a beváltó oldal jelzi hibaként. Lejáratként a
                // Globalix újranyitná, és a kör sosem érne véget.
                if (embedState()?.confirmed && !sessionExpiredRedirecting) {
                    sessionExpiredRedirecting = true;
                    markSessionEnded();
                    notifyExpired(error.response.data?.code === 'embed_superseded' ? 'replaced' : 'expired');
                    router.push({name: 'embed-expired'});
                }
                return Promise.reject(error);
            }

            // a háttérkérések egyszerre kapják a 401-et: egy üzenet és egy átirányítás, nem kérésenként
            const current = router.currentRoute.value;
            // teljes oldalbetöltéskor az első navigáció még nem zárult le: a kért cím a címsorban van
            const target = current?.matched?.length
                ? current.fullPath
                : window.location.pathname + window.location.search;

            if (current?.name !== 'login' && !target.startsWith('/login') && !sessionExpiredRedirecting) {
                sessionExpiredRedirecting = true;
                markSessionEnded();
                setTimeout(() => { sessionExpiredRedirecting = false; }, 5000);
                // egy üzenet: a belépő lapon, a form fölött; a szöveget a lap fordítja (login.vue)
                try { window.sessionStorage.setItem('xmed.session.expired', '1'); } catch (e) { /* privát mód */ }

                router.push({ name: 'login', query: target && target !== '/' ? {redirectFrom: target} : {} })
            }
        }

        // Do something with response error
        return Promise.reject(error);
    }
);

export default _axios;
